import { create } from 'zustand';
import type { ParentPairingResponse, ParentStudentLink, RedeemCodeResponse } from '@tupai/types';
import { PAIRING_CODE_LENGTH } from '@tupai/types';
import {
  getMyCode,
  regenerateMyCode,
  redeemChildCode,
  getParentLinks as fetchParentLinks,
  approveLink,
  rejectLink,
  revokeLink,
  ApiError,
} from '@/lib/api';
import { useAuthStore } from './auth-store';
import { getLanguage, i18n } from '@/lib/language';
import type { RefreshResult } from '@/lib/links-polling';
import { apiErrorMessage } from '@/lib/api-errors';

// This store lives outside React, so it reads the language with getLanguage()
// at the moment a toast fires rather than subscribing. A toast is transient —
// it never needs to survive a language switch, only to be correct when shown.
const t = () => i18n.getFixedT(getLanguage(), 'common');

interface Toast {
  type: 'success' | 'error';
  message: string;
}

// Message KEYS — a constant of resolved strings would freeze whichever
// language the app started in.
const LINK_ACTION_MESSAGES: Record<
  'approve' | 'reject' | 'revoke',
  'toastApproved' | 'toastRejected' | 'toastRevoked'
> = {
  approve: 'toastApproved',
  reject: 'toastRejected',
  revoke: 'toastRevoked',
};

const LINK_ACTIONS: Record<
  'approve' | 'reject' | 'revoke',
  (id: string, childName?: string) => Promise<void>
> = {
  approve: approveLink,
  reject: rejectLink,
  revoke: revokeLink,
};

// Every code checkStudentSeatAddition's SeatGateResult can return (billing-server).
// Only 'approve' can ever hit this — reject/revoke never call the seat gate —
// but the check lives here rather than per-action so a future gated action
// doesn't have to remember to repeat it. Mirrors LinkChildSheet's own
// allowlist for the redeem side of this same gate.
export const SEAT_GATE_CODES = new Set([
  'trial_seat_locked',
  'seat_limit_reached',
  'seat_limit_reached_max_tier',
  'seats_over_cap',
  'feature_blocked',
  'upgrade_required',
]);

interface LinksState {
  parentLinks: ParentStudentLink[];
  // Whether the initial GET /pairing/links has settled (either way). An empty
  // parentLinks array cannot stand in for "still loading": a parent who
  // genuinely owns no links has an empty array forever, which left the child
  // hub spinning indefinitely instead of showing not-found (PHUB-013).
  parentLinksLoaded: boolean;
  linkActionId: string | null;
  myCode: ParentPairingResponse | null;
  myCodeLoading: boolean;
  myCodeError: boolean;
  myCodeRegenerating: boolean;
  myCodeCopied: boolean;
  toast: Toast | null;

  refreshMyCode: () => void;
  // Never rejects. 'unauthorized' (no token, or a 401) tells the poller to
  // stop; any other failure is swallowed here and reported as 'error' so the
  // poller can back off.
  refreshParentLinks: () => Promise<RefreshResult>;
  // Resolves true on success, false on failure — callers that need to gate a
  // navigation on the outcome (e.g. the hub's unlink sheet) can await this
  // directly instead of re-deriving success from side effects.
  // onGate: only 'approve' can ever trigger it (see SEAT_GATE_CODES) — when
  // it fires, the caller renders GateNoticeModal instead of the toast, so
  // pass it whenever the approve button lives on a page that already has one.
  runLinkAction: (
    linkId: string,
    action: 'approve' | 'reject' | 'revoke',
    onGate?: (code: string) => void,
    // Only 'approve' uses it: the name the parent typed for a child with none yet.
    childName?: string
  ) => Promise<boolean>;
  handleCopyMyCode: () => void;
  handleRegenerateMyCode: () => Promise<void>;
  // Always consents (TUP-1835): the caller shows the consent step first.
  handleAddChildSubmit: (childCode: string, childName?: string) => Promise<RedeemCodeResponse>;
  showToast: (type: 'success' | 'error', message: string) => void;
  reset: () => void;
}

let toastTimer: ReturnType<typeof setTimeout> | null = null;

export const useLinksStore = create<LinksState>()((set, get) => ({
  parentLinks: [],
  parentLinksLoaded: false,
  linkActionId: null,
  myCode: null,
  myCodeLoading: false,
  myCodeError: false,
  myCodeRegenerating: false,
  myCodeCopied: false,
  toast: null,

  showToast: (type, message) => {
    if (toastTimer) clearTimeout(toastTimer);
    set({ toast: { type, message } });
    toastTimer = setTimeout(() => set({ toast: null }), 3000);
  },

  refreshMyCode: () => {
    if (!useAuthStore.getState().token) return;
    set({ myCodeLoading: true, myCodeError: false });
    getMyCode()
      .then((code) => set({ myCode: code }))
      .catch(() => set({ myCodeError: true }))
      .finally(() => set({ myCodeLoading: false }));
  },

  refreshParentLinks: async () => {
    if (!useAuthStore.getState().token) return 'unauthorized';
    try {
      const data = await fetchParentLinks();
      set({ parentLinks: data.links ?? [] });
      return 'ok';
    } catch (err) {
      // The api layer has already expired the session on a 401.
      return err instanceof ApiError && err.status === 401 ? 'unauthorized' : 'error';
    } finally {
      set({ parentLinksLoaded: true });
    }
  },

  runLinkAction: async (linkId, action, onGate, childName) => {
    set({ linkActionId: linkId });
    try {
      await LINK_ACTIONS[action](linkId, childName);
      get().refreshParentLinks();
      get().showToast('success', t()(LINK_ACTION_MESSAGES[action]));
      return true;
    } catch (err: unknown) {
      // Billing-gated approve (decision 45, claim 4): surface as a proper
      // gate notice with an upgrade path, not a raw toast — same treatment
      // LinkChildSheet gives the redeem side of this gate.
      if (onGate && err instanceof ApiError && err.code && SEAT_GATE_CODES.has(err.code)) {
        onGate(err.code);
        return false;
      }
      // 404 is its own case: the request was cancelled or handled elsewhere,
      // which is worth saying plainly instead of "that didn't work".
      const msg =
        err instanceof ApiError && err.status === 404
          ? t()('errRequestGone')
          : apiErrorMessage(err, t(), t()('errLinkActionFailed'));
      get().showToast('error', msg);
      return false;
    } finally {
      set({ linkActionId: null });
    }
  },

  handleCopyMyCode: () => {
    const { myCode } = get();
    if (!myCode) return;
    navigator.clipboard.writeText(myCode.code).then(() => {
      set({ myCodeCopied: true });
      setTimeout(() => set({ myCodeCopied: false }), 1500);
    });
  },

  handleRegenerateMyCode: async () => {
    set({ myCodeRegenerating: true });
    try {
      set({ myCode: await regenerateMyCode() });
    } catch (err: unknown) {
      get().showToast('error', apiErrorMessage(err, t(), t()('errRegenerateCode')));
    } finally {
      set({ myCodeRegenerating: false });
    }
  },

  handleAddChildSubmit: async (childCode, childName) => {
    if (childCode.length !== PAIRING_CODE_LENGTH) {
      // Caught by LinkChildSheet, which renders its own translated message —
      // this string is never displayed.
      throw new Error(`Code must be exactly ${PAIRING_CODE_LENGTH} characters.`);
    }
    const response = await redeemChildCode(childCode, childName);
    get().refreshParentLinks();
    return response;
  },

  reset: () =>
    set({
      parentLinks: [],
      parentLinksLoaded: false,
      linkActionId: null,
      myCode: null,
      myCodeLoading: false,
      myCodeError: false,
      myCodeRegenerating: false,
      myCodeCopied: false,
      toast: null,
    }),
}));
